/**
 * Jagad Jawa — Modul Domain: Pustaka Engine
 * Menangani pemuatan, pencarian, pemfilteran kategori, dan personalisasi nama
 * untuk Pustaka Dongo, Wirid & Ubarampe Jawa.
 */

import { PUSTAKA_DATA, PUSTAKA_KATEGORI, PUSTAKA_ENTRI, PUSTAKA_DATA_TERSTRUKTUR } from '../../data/pustaka-db.js';
import {
  NASKAH_KUNO_LIST,
  KAMUS_JAWA_INDONESIA,
  KAMUS_JAWA_SANSKERTA,
  DOKUMEN_REFERENSI_BUDAYA
} from '../../data/pustaka-digital-db.js';

export const PLACEHOLDER_NAMA_REGEX = /(\.{3,}|…+)/g;

/**
 * Mengambil daftar seluruh kategori pustaka.
 * @returns {Array<{id: string, nama: string, urutan: number, deskripsi: string}>}
 */
export function getPustakaKategoriList() {
  return [...PUSTAKA_KATEGORI].sort((a, b) => a.urutan - b.urutan);
}

/**
 * Mengambil detail kategori berdasarkan ID.
 * @param {string} kategoriId 
 * @returns {Object|null}
 */
export function getPustakaKategoriById(kategoriId) {
  if (!kategoriId) return null;
  return PUSTAKA_KATEGORI.find(k => k.id === kategoriId) || null;
}

/**
 * Mengambil seluruh entri pustaka.
 * @returns {Array<Object>}
 */
export function getAllPustakaEntri() {
  return [...PUSTAKA_ENTRI];
}

/**
 * Mengambil satu entri berdasarkan ID.
 * @param {string} id 
 * @returns {Object|null}
 */
export function getPustakaEntriById(id) {
  if (!id) return null;
  return PUSTAKA_ENTRI.find(e => e.id === id) || null;
}

/**
 * Memeriksa apakah suatu entri atau kumpulan baris memuat titik isian nama pengguna.
 * @param {Object|Array<string>} entriOrBaris 
 * @returns {boolean}
 */
export function hasNamePlaceholder(entriOrBaris) {
  if (!entriOrBaris) return false;
  if (Array.isArray(entriOrBaris)) {
    return entriOrBaris.some(line => PLACEHOLDER_NAMA_REGEX.test(line));
  }
  if (entriOrBaris.bagian && Array.isArray(entriOrBaris.bagian)) {
    return entriOrBaris.bagian.some(b => Array.isArray(b.baris) && b.baris.some(line => PLACEHOLDER_NAMA_REGEX.test(line)));
  }
  return false;
}

/**
 * Filter dan cari entri pustaka berdasarkan kategori dan kata kunci.
 * @param {Object} options
 * @param {string} [options.kategori] - ID kategori atau null/empty untuk semua
 * @param {string} [options.query] - Kata kunci pencarian
 * @returns {Array<Object>}
 */
export function searchPustakaEntri({ kategori = null, query = '' } = {}) {
  let list = [...PUSTAKA_ENTRI];

  if (kategori && kategori !== 'kabeh' && kategori !== 'sedaya') {
    list = list.filter(e => e.kategori === kategori);
  }

  if (query && query.trim()) {
    const q = query.trim().toLowerCase();
    list = list.filter(e => {
      // 1. Cek judul & subjudul
      if (e.judul && e.judul.toLowerCase().includes(q)) return true;
      if (e.subjudul && e.subjudul.toLowerCase().includes(q)) return true;
      // 2. Cek ringkasan
      if (e.ringkasan && e.ringkasan.toLowerCase().includes(q)) return true;
      // 3. Cek tag
      if (Array.isArray(e.tag) && e.tag.some(t => t.toLowerCase().includes(q))) return true;
      // 4. Cek teks_cari terindeks
      if (e.teks_cari && e.teks_cari.toLowerCase().includes(q)) return true;
      // 5. Cek baris dalam bagian
      if (Array.isArray(e.bagian)) {
        for (const b of e.bagian) {
          if (b.label && b.label.toLowerCase().includes(q)) return true;
          if (Array.isArray(b.baris) && b.baris.some(line => line.toLowerCase().includes(q))) return true;
        }
      }
      return false;
    });
  }

  return list;
}

/**
 * Personalisasi baris-baris doa dengan mengganti placeholder '………' dengan nama pengguna.
 * @param {Array<string>} barisList 
 * @param {string} namaPengguna 
 * @returns {Array<string>}
 */
export function personalizeDoaLines(barisList, namaPengguna = '') {
  if (!Array.isArray(barisList)) return [];
  const cleanName = namaPengguna ? namaPengguna.trim() : '';

  return barisList.map(line => {
    if (!line) return '';
    if (!cleanName) {
      return line;
    }
    return line.replace(PLACEHOLDER_NAMA_REGEX, cleanName);
  });
}

/**
 * Format teks doa lengkap dalam bentuk plain-text rapi untuk salin clipboard atau share.
 * @param {Object} entri 
 * @param {string} [namaPengguna=''] 
 * @returns {string}
 */
export function formatDoaPlainText(entri, namaPengguna = '') {
  if (!entri) return '';
  const kat = getPustakaKategoriById(entri.kategori);

  let out = `📜 *${entri.judul.toUpperCase()}*\n`;
  if (entri.subjudul) out += `_${entri.subjudul}_\n`;
  if (kat) out += `📁 Kategori: ${kat.nama}\n`;
  if (entri.waktu_pakai) out += `⏱️ Waktu: ${entri.waktu_pakai}\n`;
  if (namaPengguna && namaPengguna.trim()) {
    out += `👤 Diniyatkan Kangge: ${namaPengguna.trim()}\n`;
  }
  out += `\n──────────────────────────────\n\n`;

  if (Array.isArray(entri.bagian)) {
    entri.bagian.forEach((bag, idx) => {
      if (bag.label) {
        out += `▶ *${bag.label}*`;
        if (bag.pengulangan) out += ` (Diwaca ${bag.pengulangan}x)`;
        out += `:\n`;
      }
      const lines = personalizeDoaLines(bag.baris || [], namaPengguna);
      lines.forEach(l => {
        out += `${l}\n`;
      });
      if (bag.petunjuk) {
        out += `_Petunjuk: ${bag.petunjuk}_\n`;
      }
      if (bag.catatan) {
        out += `_Catatan: ${bag.catatan}_\n`;
      }
      out += `\n`;
    });
  }

  if (entri.penutup) {
    out += `✨ *Panutup:* ${entri.penutup}\n\n`;
  }

  out += `Kadhudhah saking Pustaka Jagad Jawa\nhttps://jagad-jawa.web.app`;
  return out;
}

/**
 * Ambil entri-entri terkait beserta alasannya.
 * @param {Object} entri 
 * @returns {Array<{entri: Object, alasan: string}>}
 */
export function getPustakaRelatedEntri(entri) {
  if (!entri || !Array.isArray(entri.terkait)) return [];
  const results = [];
  entri.terkait.forEach(item => {
    const target = getPustakaEntriById(item.id);
    if (target) {
      results.push({ entri: target, alasan: item.alasan });
    }
  });
  return results;
}

// ─── 2. FITUR DIGITAL LIBRARY: NASKAH KUNO & BABAD ─────────────────────────

function getRuntimeDigitalData(customDataset = null) {
  if (customDataset && typeof customDataset === 'object') return customDataset;
  if (typeof globalThis !== 'undefined' && globalThis._currentPustakaDigitalData) {
    return globalThis._currentPustakaDigitalData;
  }
  return null;
}

export function getAllNaskahKuno(customDataset = null) {
  const rt = getRuntimeDigitalData(customDataset);
  if (rt && Array.isArray(rt.naskah_kuno) && rt.naskah_kuno.length > 0) {
    return [...rt.naskah_kuno];
  }
  return [...NASKAH_KUNO_LIST];
}

export function getNaskahKunoById(id, customDataset = null) {
  if (!id) return null;
  const list = getAllNaskahKuno(customDataset);
  return list.find(n => n.id === id) || null;
}

export function searchNaskahKuno(query = '', customDataset = null) {
  const list = getAllNaskahKuno(customDataset);
  const q = String(query).toLowerCase().trim();
  if (!q) return list;
  return list.filter(n =>
    (n.judul && n.judul.toLowerCase().includes(q)) ||
    (n.pengarang && n.pengarang.toLowerCase().includes(q)) ||
    (n.deskripsi && n.deskripsi.toLowerCase().includes(q)) ||
    (n.isiRingkas && n.isiRingkas.toLowerCase().includes(q)) ||
    (n.babList && n.babList.some(b => 
      (b.namaBab && b.namaBab.toLowerCase().includes(q)) || 
      (b.teksJawa && b.teksJawa.toLowerCase().includes(q)) || 
      (b.terjemahan && b.terjemahan.toLowerCase().includes(q))
    ))
  );
}

export function formatNaskahPlainText(naskah) {
  if (!naskah) return '';
  const author = naskah.pengarang || naskah.penganggit || '-';
  const year = naskah.tahun || naskah.taun || '-';
  const category = naskah.kategori || naskah.subjudul || '-';
  const desc = naskah.deskripsi || naskah.isiRingkas || naskah.ringkesan || '-';

  let out = `📜 ${(naskah.judul || '').toUpperCase()}\n`;
  out += `Panganggit / Pujangga: ${author} (${year})\n`;
  out += `Kategori: ${category}\n`;
  out += `Deskripsi: ${desc}\n\n`;
  out += `====================================================\n\n`;

  const babList = naskah.babList || naskah.pupuh || naskah.bab || [];
  if (Array.isArray(babList)) {
    babList.forEach((bab, idx) => {
      const babTitle = bab.namaBab || bab.nama || bab.irah_irahan || `Bab ${idx + 1}`;
      out += `[ ${babTitle} ]\n\n`;
      if (bab.teksJawa) {
        out += `Teks Basa Jawa:\n${bab.teksJawa}\n\n`;
      } else if (Array.isArray(bab.bait_pilihan)) {
        bab.bait_pilihan.forEach((b, bIdx) => {
          out += `Bait ${b.pada || bIdx + 1}:\n${Array.isArray(b.teks) ? b.teks.join('\n') : b.teks}\n\n`;
          if (b.arti) out += `Surasa Makna: ${b.arti}\n\n`;
        });
      }
      if (bab.terjemahan) {
        out += `Terjemahan Basa Indonesia:\n${bab.terjemahan}\n\n`;
      } else if (bab.isi) {
        out += `Isi:\n${bab.isi}\n\n`;
      }
      out += `----------------------------------------------------\n\n`;
    });
  }
  return out;
}

// ─── 3. FITUR DIGITAL LIBRARY: KAMUS JAWA-INDONESIA & SANSKERTA ───────────

export function getAllKamusJawaIndo(customDataset = null) {
  const rt = getRuntimeDigitalData(customDataset);
  if (rt && Array.isArray(rt.kamus_jawa_indonesia) && rt.kamus_jawa_indonesia.length > 0) {
    return [...rt.kamus_jawa_indonesia];
  }
  return [...KAMUS_JAWA_INDONESIA];
}

export function searchKamusJawaIndo(query = '', customDataset = null) {
  const list = getAllKamusJawaIndo(customDataset);
  const q = String(query).toLowerCase().trim();
  if (!q) return list;
  return list.filter(k =>
    (k.jawa && k.jawa.toLowerCase().includes(q)) ||
    (k.krama && k.krama.toLowerCase().includes(q)) ||
    (k.id && k.id.toLowerCase().includes(q)) ||
    (k.contoh && k.contoh.toLowerCase().includes(q))
  );
}

export function getAllKamusJawaSanskerta(customDataset = null) {
  const rt = getRuntimeDigitalData(customDataset);
  if (rt && Array.isArray(rt.kamus_jawa_sanskerta) && rt.kamus_jawa_sanskerta.length > 0) {
    return [...rt.kamus_jawa_sanskerta];
  }
  return [...KAMUS_JAWA_SANSKERTA];
}

export function searchKamusJawaSanskerta(query = '', customDataset = null) {
  const list = getAllKamusJawaSanskerta(customDataset);
  const q = String(query).toLowerCase().trim();
  if (!q) return list;
  return list.filter(k =>
    (k.sanskerta && k.sanskerta.toLowerCase().includes(q)) ||
    (k.jawa && k.jawa.toLowerCase().includes(q)) ||
    (k.makna && k.makna.toLowerCase().includes(q))
  );
}

export function formatKamusPlainText(list, jenis = 'jawa-indo') {
  if (!Array.isArray(list)) return '';
  let out = `📚 KAMUS ${jenis === 'sanskerta' ? 'JAWA - SANSKERTA' : 'JAWA - INDONESIA'} — JAGAD JAWA\n`;
  out += `Gunggunging Lema / Kosakata: ${list.length} tembung\n`;
  out += `====================================================\n\n`;

  if (jenis === 'sanskerta') {
    list.forEach((item, idx) => {
      out += `${idx + 1}. ${item.sanskerta} ➔ ${item.jawa}\n   Makna: ${item.makna}\n\n`;
    });
  } else {
    list.forEach((item, idx) => {
      out += `${idx + 1}. ${item.jawa} (Krama: ${item.krama}) = ${item.id}\n   Tuladha: "${item.contoh}"\n\n`;
    });
  }

  out += `\nKadhudhah saking Pustaka Digital — Jagad Jawa Nusantara\nhttps://jagad-jawa.web.app`;
  return out;
}

// ─── 4. FITUR DIGITAL LIBRARY: DOKUMEN REFERENSI KEBUDAYAAN ──────────────

export function getAllDokumenReferensi(customDataset = null) {
  const rt = getRuntimeDigitalData(customDataset);
  if (rt && Array.isArray(rt.dokumen_referensi) && rt.dokumen_referensi.length > 0) {
    return [...rt.dokumen_referensi];
  }
  return [...DOKUMEN_REFERENSI_BUDAYA];
}

export function getDokumenReferensiById(id, customDataset = null) {
  if (!id) return null;
  const list = getAllDokumenReferensi(customDataset);
  return list.find(d => d.id === id) || null;
}

export function searchDokumenReferensi(query = '', customDataset = null) {
  const list = getAllDokumenReferensi(customDataset);
  const q = String(query).toLowerCase().trim();
  if (!q) return list;
  return list.filter(d =>
    (d.judul && d.judul.toLowerCase().includes(q)) ||
    (d.kategori && d.kategori.toLowerCase().includes(q)) ||
    (d.deskripsi && d.deskripsi.toLowerCase().includes(q)) ||
    (d.ringkasan && d.ringkasan.toLowerCase().includes(q)) ||
    (d.kontenTeks && d.kontenTeks.toLowerCase().includes(q))
  );
}

export const getAllKamusJawaIndonesia = getAllKamusJawaIndo;
export const searchKamusJawaIndonesia = searchKamusJawaIndo;

export function formatKamusEntryPlainText(item, type = 'indonesia') {
  if (!item) return '';
  if (type === 'sanskerta') {
    return `${item.sanskerta} ➔ ${item.jawa || item.istilah}\n   Arti: ${item.arti_harfiah || item.makna_filosofis || item.makna}`;
  }
  return `${item.jawa || item.kata} (Krama: ${item.krama || item.krama_inggil || '-'}) = ${item.indonesia || item.id || item.makna}\n   Tuladha: "${item.conto_ukara || item.contoh || '-'}"`;
}

export function formatDokumenReferensiPlainText(doc) {
  if (!doc) return '';
  let out = `📜 ${doc.judul?.toUpperCase() || 'DOKUMEN REFERENSI'}\n`;
  if (doc.subjudul) out += `${doc.subjudul}\n`;
  out += `Kategori: ${doc.kategori || '-'}\n`;
  out += `====================================================\n\n`;
  if (doc.ringkasan || doc.deskripsi) {
    out += `Ringkasan:\n${doc.ringkasan || doc.deskripsi}\n\n`;
  }
  if (doc.kontenTeks) {
    out += `${doc.kontenTeks}\n\n`;
  }
  if (Array.isArray(doc.pupuh_list)) {
    out += `DAFTAR PAUGERAN PUPUH MACAPAT:\n`;
    doc.pupuh_list.forEach((p, idx) => {
      out += `${idx + 1}. ${p.nama} (${p.guru_gatra} Gatra) - ${p.guru_wilangan_lagu}\n   Watak: ${p.watak}\n\n`;
    });
  }
  if (doc.laras) {
    out += `LARAS & PATHET GAMELAN:\n`;
    if (doc.laras.slendro) {
      out += `• Slendro: ${doc.laras.slendro.deskripsi} (Pathet: ${doc.laras.slendro.pathet?.join(', ')})\n`;
    }
    if (doc.laras.pelog) {
      out += `• Pelog: ${doc.laras.pelog.deskripsi} (Pathet: ${doc.laras.pelog.pathet?.join(', ')})\n`;
    }
    out += `\n`;
  }
  if (Array.isArray(doc.motif_larangan)) {
    out += `MOTIF BATIK LARANGAN:\n`;
    doc.motif_larangan.forEach((m, idx) => {
      out += `${idx + 1}. ${m.nama} (${m.peruntukan}): ${m.filosofi}\n`;
    });
    out += `\n`;
  }
  out += `\nKadhudhah saking Pustaka Digital — Jagad Jawa Nusantara\nhttps://jagad-jawa.web.app`;
  return out;
}

export {
  PUSTAKA_DATA,
  PUSTAKA_KATEGORI,
  PUSTAKA_ENTRI,
  PUSTAKA_DATA_TERSTRUKTUR,
  NASKAH_KUNO_LIST,
  KAMUS_JAWA_INDONESIA,
  KAMUS_JAWA_SANSKERTA,
  DOKUMEN_REFERENSI_BUDAYA
};
